import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  useMediaQuery,
  Alert,
  AlertTitle,
} from "@mui/material";
import { useCharactersContext } from "../hooks/Context";

interface Question {
  updatedBy: string;
  question: string;
  mediaUrl: string;
  difficultyLevel: string;
  answerType: number;
  active: boolean;
  marks: number;
  updatedDate: string;
  subjectId: number;
  questionBankId: number;
  classId: number;
  createdDate: string;
  createdBy: string;
  optionsList: { optionId: number; option: string; correctAnswer: string | boolean }[];
  questionPaperId: number;
  uiType: number;
  remarks: string;
}

interface LayoutProps {
  Questions: Question[];
  sectionName: string;
  currentQuestionIndexProp?: number;
}

const Layout20: React.FC<LayoutProps> = ({
  Questions,
  sectionName,
  currentQuestionIndexProp,
}) => {
  const [internalQuestionIndex, setInternalQuestionIndex] = useState(0);
  
  const currentQuestionIndex: number = currentQuestionIndexProp !== undefined 
    ? currentQuestionIndexProp 
    : internalQuestionIndex;
  
  const isGrammarMode = currentQuestionIndexProp !== undefined;
  const {
    questionScores,
    setQuestionScores,
    isViewMode,
    examAnswers,
    setExamAnswers,
  } = useCharactersContext();
  
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width:820px)");
  const [showAlert, setShowAlert] = useState(false);
  const handleAlertClose = () => setShowAlert(false);
  setTimeout(handleAlertClose, 3000);

  const currentQuestion = Questions[currentQuestionIndex];
  const sentences = currentQuestion?.question.split("<br>");
  const instructionText = sentences[0] || "Click and drag the correct option.";
  const questionSentence = sentences[1] || "";
  
  const options = currentQuestion?.optionsList ?? [];
  const correctAnswer = options.find(opt => opt.correctAnswer === true || opt.correctAnswer === "true")?.option || "";

  const [droppedWord, setDroppedWord] = useState<string | null>(null);
  const [draggedWord, setDraggedWord] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  // Load dropped word from localStorage when question changes
  useEffect(() => {
    if (currentQuestion) {
      const storageKey = `droppedWordLayout20_${currentQuestion.questionPaperId}_${currentQuestion.questionBankId}`;
      const savedWord = localStorage.getItem(storageKey);
      setDroppedWord(savedWord ? JSON.parse(savedWord) : null);
    }
  }, [currentQuestion?.questionBankId, currentQuestion?.questionPaperId]);

  // Handle view mode - show correct answers
  useEffect(() => {
    if (isViewMode && currentQuestion) {
      const correctOption = options.find(opt => {
        const answer = opt.correctAnswer;
        return answer === "true" || answer === true || answer === "True" || String(answer).toLowerCase() === "true";
      });
      if (correctOption) {
        setDroppedWord(correctOption.option);
      }
    }
  }, [currentQuestion, isViewMode, options]);

  // Replace blank with dropped word or show blank placeholder
  const renderSentence = () => {
    const blankPattern = /_{3,}/g; // Matches 3 or more underscores
    if (droppedWord) {
      const parts = questionSentence.split(blankPattern);
      if (parts.length > 1) {
        return (
          <>
            {parts[0]}
            <Box
              component="span"
              sx={{
                color: "#2196f3",
                padding: "2px 6px",
                borderRadius: "4px",
                fontWeight: "600",
              }}
            >
              {droppedWord}
            </Box>
            {parts[1]}
          </>
        );
      }
      return questionSentence.replace(blankPattern, droppedWord);
    }
    return questionSentence.replace(blankPattern, "_________");
  };

  const allowDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsHovering(true);
  };

  const handleDragLeave = () => {
    setIsHovering(false);
  };

  const drag = (event: React.DragEvent<HTMLDivElement>, word: string) => {
    setDraggedWord(word);
    event.dataTransfer.effectAllowed = "move";
  };

  const drop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsHovering(false);
    if (draggedWord) {
      setDroppedWord(draggedWord);
      const storageKey = `droppedWordLayout20_${currentQuestion.questionPaperId}_${currentQuestion.questionBankId}`;
      localStorage.setItem(storageKey, JSON.stringify(draggedWord));
    }
    setDraggedWord(null);
  };

  const touchStart = (event: React.TouchEvent<HTMLDivElement>, word: string) => {
    setDraggedWord(word);
  };

  const touchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (draggedWord) {
      const touchX = event.changedTouches[0].clientX;
      const touchY = event.changedTouches[0].clientY;
      const dropZone = document.getElementById("dropZone");
      
      if (dropZone) {
        const rect = dropZone.getBoundingClientRect();
        const isInside =
          touchX >= rect.left &&
          touchX <= rect.right &&
          touchY >= rect.top &&
          touchY <= rect.bottom;

        if (isInside) {
          setDroppedWord(draggedWord);
          const storageKey = `droppedWordLayout20_${currentQuestion.questionPaperId}_${currentQuestion.questionBankId}`;
          localStorage.setItem(storageKey, JSON.stringify(draggedWord));
        }
      }
    }
    setDraggedWord(null);
    setIsHovering(false);
  };

  const handleFormSubmit = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setShowAlert(true);

    const isCorrect = droppedWord?.toLowerCase().trim() === correctAnswer?.toLowerCase().trim();
    
    const questionIndex = questionScores.findIndex(
      (item) => item.questionBankId === currentQuestion.questionBankId
    );

    if (isCorrect) {
      if (questionIndex !== -1) {
        const updatedScores = [...questionScores];
        updatedScores[questionIndex] = {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: currentQuestion.marks,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        };
        setQuestionScores(updatedScores);
      } else {
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: currentQuestion.marks,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    } else {
      if (questionIndex === -1) {
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: 0,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      } else {
        const updatedScores = [...questionScores];
        updatedScores[questionIndex] = {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: 0,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        };
        setQuestionScores(updatedScores);
      }
    }

    const updatedExamAnswer = {
      questionBankId: currentQuestion.questionBankId,
      acutalOptionValue: correctAnswer,
      enteredOptionValue: droppedWord || "",
    };

    const existingAnswerIndex = examAnswers.findIndex(
      (answer) => answer.questionBankId === currentQuestion.questionBankId
    );

    if (existingAnswerIndex !== -1) {
      setExamAnswers((prevAnswers) =>
        prevAnswers.map((answer, index) =>
          index === existingAnswerIndex ? { ...updatedExamAnswer } : answer
        )
      );
    } else {
      setExamAnswers((prevAnswers) => [...prevAnswers, updatedExamAnswer]);
    }
  };

  const resetQuestion = () => {
    setDroppedWord(null);
    setDraggedWord(null);
    setIsHovering(false);
    const storageKey = `droppedWordLayout20_${currentQuestion.questionPaperId}_${currentQuestion.questionBankId}`;
    localStorage.removeItem(storageKey);
  };

  // Get available options (exclude the one already dropped)
  const availableOptions = options.filter(opt => opt.option !== droppedWord);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "auto",
        marginTop: isViewMode ? "5%" : isTablet ? "15%" : "auto",
        padding: isMobile ? "10px" : "20px",
      }}
    >
      {showAlert && (
        <Alert
          severity="success"
          variant="filled"
          sx={{
            position: "fixed",
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "0.5",
          }}
        >
          <AlertTitle sx={{ color: "white" }}>Answer submitted</AlertTitle>
        </Alert>
      )}
      {currentQuestion && (
        <Card
          variant="outlined"
          sx={{
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
            pointerEvents: isViewMode ? "none" : "auto",
            width: "100%",
            maxWidth: "800px",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingBottom: "0px",
              }}
              style={{ userSelect: "none" }}
            >
              {/* Instruction text */}
              <Typography
                sx={{
                  fontWeight: "700",
                  fontSize: isMobile ? "90%" : "110%",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                {instructionText}
              </Typography>

              {/* Sentence with blank */}
              <Box
                id="dropZone"
                onDrop={drop}
                onDragOver={allowDrop}
                onDragLeave={handleDragLeave}
                onTouchEnd={touchEnd}
                sx={{
                  minHeight: "60px",
                  padding: "15px 20px",
                  marginBottom: "30px",
                  border: isHovering ? "3px dashed #2196f3" : "2px dashed #ccc",
                  borderRadius: "8px",
                  backgroundColor: isHovering ? "#e3f2fd" : "#f5f5f5",
                  transition: "all 0.3s ease",
                  width: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? "16px" : "20px",
                    fontWeight: "500",
                    textAlign: "center",
                    color: "#000",
                  }}
                >
                  {renderSentence()}
                </Typography>
              </Box>

              {/* Draggable options */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: "15px",
                  marginBottom: "20px",
                  width: "100%",
                }}
              >
                {options.map((option, index) => {
                  const isDropped = droppedWord === option.option;
                  const isDragging = draggedWord === option.option;
                  
                  return (
                    <Box
                      key={option.optionId || index}
                      draggable={!isDropped && !isViewMode}
                      onDragStart={(e) => drag(e, option.option)}
                      onTouchStart={(e) => touchStart(e, option.option)}
                      onTouchEnd={touchEnd}
                      sx={{
                        padding: "12px 24px",
                        backgroundColor: isDropped 
                          ? "#bdbdbd" 
                          : isDragging 
                          ? "#bbdefb" 
                          : "#2196f3",
                        color: isDropped ? "#424242" : "#fff",
                        borderRadius: "8px",
                        cursor: isDropped || isViewMode ? "not-allowed" : "grab",
                        opacity: isDropped ? 0.8 : 1,
                        fontSize: isMobile ? "14px" : "16px",
                        fontWeight: "600",
                        boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
                        transition: "all 0.2s ease",
                        userSelect: "none",
                        pointerEvents: isDropped || isViewMode ? "none" : "auto",
                        "&:active": {
                          cursor: "grabbing",
                          transform: "scale(0.95)",
                        },
                      }}
                    >
                      {option.option}
                    </Box>
                  );
                })}
              </Box>

              {/* Show correct answer in view mode */}
              {isViewMode && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "row",
                    margin: 1,
                  }}
                >
                  <Chip
                    label={`Correct answer: "${correctAnswer}"`}
                    sx={{ backgroundColor: "#81c784", color: "#fff", fontWeight: "bold" }}
                  />
                </Box>
              )}

              {/* Submit and Reset buttons */}
              {!isViewMode && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "row",
                    marginTop: "20px",
                    gap: "10px",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFormSubmit}
                    disabled={!droppedWord}
                  >
                    Submit
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={resetQuestion}
                    disabled={!droppedWord}
                  >
                    Reset
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Layout20;

